import React, { useCallback, useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import { api } from "../helpers/api"
import { t } from "../helpers/format"
import DrawerHeader   from "./DrawerHeader"
import StatusTimeline from "./StatusTimeline"
import EntryRow       from "./EntryRow"
import AllowancesCard from "./AllowancesCard"
import DrawerFooter   from "./DrawerFooter"
import DrawerSkeleton from "./DrawerSkeleton"
import Section        from "./Section"

const EXIT_MS = 220

let newRowSeq = 0

const DayDrawer = ({ target, board, urls, onClose, onMove, onChange, notify }) => {
  const [shown,    setShown]    = useState(target)
  const [visible,  setVisible]  = useState(false)
  const [day,      setDay]      = useState(null)
  const [error,    setError]    = useState(null)
  const [busy,     setBusy]     = useState(null)
  const [drafts,   setDrafts]   = useState([])
  const [approved, setApproved] = useState(false)
  const ticket = useRef(0)

  useEffect(() => {
    if (target) {
      setShown(target)
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = setTimeout(() => setShown(null), EXIT_MS)
    return () => clearTimeout(timer)
  }, [target])

  const params = shown ? { user_id: shown.user.id, date: shown.date } : {}

  const load = useCallback(() => {
    if (!target) return
    const mine = ++ticket.current
    setDay(null)
    setError(null)
    setDrafts([])
    api.get(urls.day_url, { user_id: target.user.id, date: target.date })
      .then(response => { if (mine === ticket.current) setDay(response.day) })
      .catch(err => { if (mine === ticket.current) setError(err.message) })
  }, [target?.user?.id, target?.date])

  useEffect(load, [load])

  useEffect(() => {
    if (!target) return
    const onKey = e => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [target, onClose])

  // Every write answers with the whole day, which replaces the board's copy.
  const run = (name, request, { success } = {}) => {
    setBusy(name)
    setError(null)
    return request()
      .then(response => {
        setDay(response.day)
        onChange()
        if (success) notify(success)
        return response.day
      })
      .catch(err => { setError(err.message); throw err })
      .finally(() => setBusy(null))
  }

  const saveEntry = (entry, attributes) => {
    if (entry.id) {
      return run(`entry-${entry.id}`, () => api.patch(`${urls.entries_url}/${entry.id}`, params, { entry: attributes }))
    }
    const body = { entry: { ...attributes, project_assignment_id: entry.assignment_id || undefined } }
    return run(`entry-${entry.key || entry.assignment_id}`, () => api.post(urls.entries_url, params, body))
      .then(result => { if (entry.key) setDrafts(list => list.filter(draft => draft.key !== entry.key)); return result })
  }

  const deleteEntry = entry =>
    run(`entry-${entry.id}`, () => api.delete(`${urls.entries_url}/${entry.id}`, params)).catch(() => {})

  const updateDay = attributes =>
    run("day", () => api.patch(urls.day_url, params, { day: attributes })).catch(() => {})

  const approve = () =>
    run("approve", () => api.post(urls.approve_url, params, {}), { success: t("toasts.day_approved") })
      .then(() => { setApproved(true); setTimeout(() => setApproved(false), 1400) })
      .catch(() => {})

  const reject = comment =>
    run("reject", () => api.post(urls.reject_url, params, { comment }), { success: t("toasts.day_rejected") })

  const reopen = () =>
    run("reopen", () => api.post(urls.reopen_url, params, {}), { success: t("toasts.day_reopened") }).catch(() => {})

  const addDraft = () => {
    const last = day?.entries?.[day.entries.length - 1] || day?.assignments?.[0]
    setDrafts(list => [...list, {
      key:        `new-${++newRowSeq}`,
      id:         null,
      draft:      true,
      kind:       "work",
      project_id: last?.project_id || null,
      task_id:    null,
      break_minutes: 0,
    }])
  }

  if (!shown) return null

  const editable = !!day?.can?.edit
  const entries  = day?.entries || []

  return (
    <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true" aria-label={t("drawer.label")}>
      <div
        className = {`absolute inset-0 bg-slate-900/25 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick   = {onClose}
      />
      <aside
        className = {`absolute top-0 right-0 h-full w-full max-w-[600px] flex flex-col bg-white border-l border-slate-200 transition-transform duration-200 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}
      >
        <DrawerHeader
          target   = {shown}
          day      = {day}
          approved = {approved}
          onClose  = {onClose}
          onMove   = {onMove}
          settings = {board.settings}
        />

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-6 mt-4 flex items-start gap-2.5 p-3 rounded-lg bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 animate-[hb-rise_160ms_ease-out]">
              <FontAwesomeIcon icon="circle-exclamation" className="mt-[2px]" />
              <span className="flex-1">{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-rose-400 hover:text-rose-700">
                <FontAwesomeIcon icon="xmark" />
              </button>
            </div>
          )}

          {!day ? (!error && <DrawerSkeleton />) : (
            <div className="px-6 py-5 flex flex-col gap-6">
              <StatusTimeline day={day} />

              <Section
                title  = {t("drawer.entries")}
                aside  = {editable && (
                  <Button variant="dashed-pill" size="xs" onClick={addDraft}>
                    <FontAwesomeIcon icon="plus" className="text-[9px]" />
                    {t("drawer.add_entry")}
                  </Button>
                )}
              >
                {entries.length === 0 && drafts.length === 0 ? (
                  <div className="flex flex-col items-center py-8 rounded-lg border border-dashed border-slate-200 text-center">
                    <FontAwesomeIcon icon="clock" className="text-slate-300 text-[20px] mb-2" />
                    <div className="text-[13px] font-medium text-slate-600">{t("drawer.no_entries")}</div>
                    {editable && (
                      <Button variant="secondary" size="sm" className="mt-3" onClick={addDraft}>
                        <FontAwesomeIcon icon="plus" className="text-slate-400" />
                        {t("drawer.add_entry")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {entries.map((entry, index) => (
                      <EntryRow
                        key      = {entry.id || `planned-${entry.assignment_id || index}`}
                        entry    = {entry}
                        projects = {board.projects}
                        settings = {board.settings}
                        editable = {editable}
                        busy     = {busy === `entry-${entry.id || entry.assignment_id}`}
                        onSave   = {attributes => saveEntry(entry, attributes)}
                        onDelete = {() => deleteEntry(entry)}
                      />
                    ))}
                    {drafts.map(draft => (
                      <EntryRow
                        key      = {draft.key}
                        entry    = {draft}
                        projects = {board.projects}
                        settings = {board.settings}
                        editable = {editable}
                        busy     = {busy === `entry-${draft.key}`}
                        onSave   = {attributes => saveEntry(draft, attributes)}
                        onDelete = {() => setDrafts(list => list.filter(item => item.key !== draft.key))}
                      />
                    ))}
                  </div>
                )}
              </Section>

              <AllowancesCard
                day      = {day}
                zones    = {board.settings?.zones || []}
                editable = {editable}
                busy     = {busy === "day"}
                onUpdate = {updateDay}
              />
            </div>
          )}
        </div>

        {day && (
          <DrawerFooter
            day      = {day}
            busy     = {busy}
            onApprove = {approve}
            onReject  = {reject}
            onReopen  = {reopen}
          />
        )}
      </aside>
    </div>
  )
}

export default DayDrawer
